/**
 * MUTAGIM.SHOP — Reviews Page
 * Page dédiée affichant tous les avis clients
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Star, Filter, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  if (imagePath.startsWith('/api/')) return `${API_URL}${imagePath}`;
  return `${API_URL}/api/uploads/${imagePath}`;
};

export function ReviewsPage() {
  const { i18n, t } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratingFilter, setRatingFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({ total: 0, average: 0, distribution: {} });
  
  const lang = i18n.language?.split('-')[0] || 'fr';
  const REVIEWS_PER_PAGE = 12;

  // Fetch reviews
  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: REVIEWS_PER_PAGE.toString()
      });
      
      if (ratingFilter !== 'all') {
        params.append('rating', ratingFilter);
      }
      
      const res = await fetch(`${API_URL}/api/reviews/all?${params}`);
      if (res.ok) {
        const data = await res.json();
        setReviews(data.reviews || []);
        setTotalPages(data.pages || 1);
        setStats({
          total: data.total || 0,
          average: data.average_rating || 0,
          distribution: data.rating_distribution || {}
        });
      }
    } catch (err) {
      console.error('Error fetching reviews:', err);
    } finally {
      setLoading(false);
    }
  }, [page, ratingFilter]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // Render stars
  const renderStars = (rating, size = 'md') => {
    const sizeClasses = size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${sizeClasses} ${
              star <= rating 
                ? 'text-yellow-400 fill-yellow-400' 
                : 'text-gray-600'
            }`}
          />
        ))}
      </div>
    );
  };

  // Get content in current language
  const getContent = (content) => {
    if (!content) return '';
    if (typeof content === 'string') return content;
    return content[lang] || content.fr || content.en || '';
  };

  // Get product name
  const getProductName = (product) => {
    if (!product?.name) return '';
    if (typeof product.name === 'string') return product.name;
    return product.name[lang] || product.name.fr || product.name.en || '';
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(lang === 'he' ? 'he-IL' : lang === 'en' ? 'en-US' : 'fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-mutagim-dark" data-testid="reviews-page">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <div className="bg-gradient-to-b from-zinc-900 to-mutagim-dark py-12">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {lang === 'fr' ? 'Avis Clients' : lang === 'en' ? 'Customer Reviews' : 'ביקורות לקוחות'}
            </h1>
            <p className="text-gray-400 max-w-2xl mx-auto">
              {lang === 'fr' 
                ? 'Découvrez ce que notre communauté pense de nos produits' 
                : lang === 'en' 
                  ? 'Discover what our community thinks about our products'
                  : 'גלה מה הקהילה שלנו חושבת על המוצרים שלנו'}
            </p>
            
            {/* Stats */}
            <div className="flex justify-center items-center gap-8 mt-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-white">{stats.total}</div>
                <div className="text-sm text-gray-400">
                  {lang === 'fr' ? 'Avis vérifiés' : lang === 'en' ? 'Verified reviews' : 'ביקורות מאומתות'}
                </div>
              </div>
              <div className="h-12 w-px bg-zinc-700" />
              <div className="text-center">
                <div className="flex items-center gap-2">
                  <span className="text-4xl font-bold text-white">{stats.average.toFixed(1)}</span>
                  <Star className="w-8 h-8 text-yellow-400 fill-yellow-400" />
                </div>
                <div className="text-sm text-gray-400">
                  {lang === 'fr' ? 'Note moyenne' : lang === 'en' ? 'Average rating' : 'דירוג ממוצע'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <span className="text-gray-400 text-sm">
                {lang === 'fr' ? 'Filtrer par note:' : lang === 'en' ? 'Filter by rating:' : 'סנן לפי דירוג:'}
              </span>
              <Select value={ratingFilter} onValueChange={(v) => { setRatingFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[140px] bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {lang === 'fr' ? 'Tous les avis' : lang === 'en' ? 'All reviews' : 'כל הביקורות'}
                  </SelectItem>
                  <SelectItem value="5">★★★★★ (5)</SelectItem>
                  <SelectItem value="4">★★★★☆ (4+)</SelectItem>
                  <SelectItem value="3">★★★☆☆ (3+)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Rating Distribution */}
            <div className="flex items-center gap-4">
              {[5, 4, 3, 2, 1].map((rating) => (
                <button
                  key={rating}
                  onClick={() => { setRatingFilter(rating.toString()); setPage(1); }}
                  className={`flex items-center gap-1 px-2 py-1 rounded text-sm transition-colors ${
                    ratingFilter === rating.toString()
                      ? 'bg-yellow-500/20 text-yellow-400'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <span>{rating}</span>
                  <Star className="w-3 h-3 fill-current" />
                  <span className="text-xs">({stats.distribution[rating] || 0})</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="container mx-auto px-4 pb-12">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-400">
                {lang === 'fr' ? 'Aucun avis trouvé' : lang === 'en' ? 'No reviews found' : 'לא נמצאו ביקורות'}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {reviews.map((review) => (
                  <div
                    key={review.id}
                    className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 hover:border-zinc-700 transition-colors"
                    data-testid={`review-card-${review.id}`}
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {renderStars(review.rating)}
                          <span className="text-xs text-gray-500">
                            {formatDate(review.created_at)}
                          </span>
                        </div>
                        <p className="text-white font-medium">{review.author_name}</p>
                      </div>
                      {review.verified_purchase && (
                        <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">
                          {lang === 'fr' ? 'Achat vérifié' : lang === 'en' ? 'Verified' : 'מאומת'}
                        </span>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="mb-4">
                      {review.title && (
                        <h4 className="text-white font-semibold mb-2">{review.title}</h4>
                      )}
                      <p className="text-gray-300 text-sm leading-relaxed">
                        {getContent(review.content)}
                      </p>
                    </div>
                    
                    {/* Product Link */}
                    {review.product && review.product.slug && (
                      <Link
                        to={`/product/${review.product.slug}`}
                        className="flex items-center gap-3 pt-4 border-t border-zinc-800 group"
                      >
                        {review.product.image && (
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0">
                            <img 
                              src={getImageUrl(review.product.image)}
                              alt={getProductName(review.product)}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-400 truncate group-hover:text-white transition-colors">
                            {getProductName(review.product)}
                          </p>
                          <p className="text-xs text-indigo-400 group-hover:text-indigo-300 transition-colors">
                            {lang === 'fr' ? 'Voir le produit →' : lang === 'en' ? 'View product →' : 'צפה במוצר →'}
                          </p>
                        </div>
                      </Link>
                    )}
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-8">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="border-zinc-700 hover:bg-zinc-800"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    {lang === 'fr' ? 'Précédent' : lang === 'en' ? 'Previous' : 'הקודם'}
                  </Button>
                  
                  <span className="text-gray-400 text-sm">
                    {page} / {totalPages}
                  </span>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="border-zinc-700 hover:bg-zinc-800"
                  >
                    {lang === 'fr' ? 'Suivant' : lang === 'en' ? 'Next' : 'הבא'}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default ReviewsPage;
